import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import { useTheme } from "@mui/material/styles";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import NoTableEntries from "@components/DataTable/NoTableEntries";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import LoadingArrowOverlay from "@components/LoadingArrowOverlay/LoadingArrowOverlay";
import SideDrawer from "@components/SideDrawer/SideDrawer";
import { useNavigationGuard } from "@context/NavigationGuardContext";
import { FORM_FIELDS } from "@features/vessel-call/create/constants";
import type { VesselCallOutletContext } from "@features/vessel-call/create/CreateVesselCallContainer";
import type { CrewFormSchema } from "@features/vessel-call/create/schema/CrewForm.schema";
import {
  useVesselCallFormStore,
  type CrewMember,
} from "@features/vessel-call/create/store/useVesselCallForm.store";
import { templateDownloadImport } from "@features/vessel-call/create/utils/templateDownloadImport";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Nationality } from "@services/vessel-call/types";
import type { Country } from "@services/vessel/types";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { useImportCrewList } from "./useImportCrewList";

const MASTER_RANK = "MASTER";

const normalizeCrewPassportNumber = (value: string) => value.trim().toUpperCase();

const isMasterRank = (rank: string) => rank.trim().toUpperCase() === MASTER_RANK;

const sortCrewWithMasterFirst = (members: CrewMember[]) =>
  members
    .map((member, index) => ({ member, index }))
    .sort((left, right) => {
      const leftIsMaster = isMasterRank(left.member.rank);
      const rightIsMaster = isMasterRank(right.member.rank);

      if (leftIsMaster !== rightIsMaster) {
        return leftIsMaster ? -1 : 1;
      }

      return left.index - right.index;
    })
    .map(({ member }) => member);

const FalForm5: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const handleDownloadTemplate = async () => {
    try {
      await templateDownloadImport({
        templateType: "crew-list",
        fileName: "crew-template.xlsx",
      });
    } catch (error) {
      console.error("Crew template download failed", error);
    }
  };

  const { formData } = useOutletContext<VesselCallOutletContext>();
  const {
    formState: { errors },
    clearErrors,
    setValue,
  } = formData;

  const crewHasError = Boolean((errors as any)?.crew);

  const syncCrewField = (nextCrew: CrewMember[]) => {
    setValue("crew", nextCrew, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const store = useVesselCallFormStore();
  const { crew, addCrew, addCrewDraft, setAddCrewDraft, clearAddCrewDraft, setField } = store;
  const importFileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const handleImportSuccess = useCallback(
    (importedCrew: CrewMember[]) => {
      setValue("crew", importedCrew, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors("crew");
    },
    [clearErrors, setValue],
  );
  const importCrewListMutation = useImportCrewList({ onSuccess: handleImportSuccess });
  const { setNavigationBlocker } = useNavigationGuard();

  useEffect(() => {
    setNavigationBlocker(
      "fal-form-5-import",
      importCrewListMutation.isPending
        ? {
            title: t("vessel-call.notifications.import-in-progress-title"),
            description: t("vessel-call.notifications.import-in-progress-description"),
            cancelButtonText: t("common.no"),
            confirmButtonText: t("common.yes"),
            onConfirm: importCrewListMutation.abortImport,
          }
        : null,
    );

    return () => setNavigationBlocker("fal-form-5-import", null);
  }, [importCrewListMutation.abortImport, importCrewListMutation.isPending, setNavigationBlocker]);

  const { options: nationalities } = useFormattedReference<Nationality>(
    REFERENCE_DATA_TYPES.NATIONALITY,
    "bff-gatepass",
  );

  const { options: countries } = useFormattedReference<Country>(
    REFERENCE_DATA_TYPES.COUNTRIES,
    "bff-gatepass",
  );

  const getNationalityNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    nationalities.forEach((nationality) => {
      if (!nationality?.nationality) return;
      const code = nationality.countryCode ?? nationality.nationality;
      map.set(code, nationality.nationality);
    });
    return map;
  }, [nationalities]);

  const getCountryNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    countries.forEach((country) => {
      if (!country?.countryName) return;
      const code = country.countryCode ?? country.countryName;
      map.set(code, country.countryName);
    });
    return map;
  }, [countries]);

  const getCountryCodeByName = useMemo(() => {
    const map = new Map<string, string>();
    countries.forEach((country) => {
      if (country.countryName && country.countryCode) {
        map.set(country.countryName, country.countryCode);
      }
    });
    return map;
  }, [countries]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<CrewMember | null>(null);

  const openAddDrawer = () => {
    setEditingMember(null);
    setIsDrawerOpen(true);
  };
  const openEditDrawer = (row: CrewMember) => {
    setEditingMember({
      ...row,
      issuingState: getCountryCodeByName.get(row.issuingState) ?? row.issuingState,
    });
    setIsDrawerOpen(true);
  };
  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const openImportFileBrowser = () => {
    if (importCrewListMutation.isPending) return;
    importFileInputRef.current?.click();
  };

  const closeImportDialog = () => {
    if (importCrewListMutation.isPending) return;
    setSelectedImportFile(null);
  };

  const handleImportFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const [file] = Array.from(event.target.files ?? []);

    if (file) {
      setSelectedImportFile(file);
    }

    event.target.value = "";
  };

  const handleImportConfirm = () => {
    if (!selectedImportFile || importCrewListMutation.isPending) return;

    const fileToImport = selectedImportFile;
    setPage(1);
    setSelectedImportFile(null);
    importCrewListMutation.mutate(fileToImport);
  };

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handlePersistCrewDraft = useCallback(
    (draft: Partial<CrewFormSchema> | null) => {
      if (!editingMember) {
        setAddCrewDraft(draft);
      }
    },
    [editingMember, setAddCrewDraft],
  );

  const headers: Header[] = useMemo(
    () => [
      { field: "no", columnName: t("vessel-call.create.table-headers.no"), minWidth: 64, flex: 0 },
      {
        field: "familyName",
        columnName: t("vessel-call.create.table-headers.family-name"),
        minWidth: 100,
      },
      {
        field: "givenName",
        columnName: t("vessel-call.create.table-headers.given-name"),
        minWidth: 100,
      },
      {
        field: "rank",
        columnName: t("vessel-call.create.table-headers.rank"),
        minWidth: 160,
      },
      {
        field: "nationality",
        columnName: t("vessel-call.create.table-headers.nationality"),
        minWidth: 140,
      },
      {
        field: "dateOfBirth",
        columnName: t("vessel-call.create.table-headers.date-of-birth"),
        type: "date",
        minWidth: 140,
      },
      {
        field: "placeOfBirth",
        columnName: t("vessel-call.create.table-headers.place-of-birth"),
        minWidth: 160,
      },
      { field: "gender", columnName: t("vessel-call.create.table-headers.gender"), minWidth: 100 },
      {
        field: "passportNumber",
        columnName: t("vessel-call.create.table-headers.passport-number"),
        minWidth: 180,
      },
      {
        field: "issuingState",
        columnName: t("vessel-call.create.table-headers.passport-issuing-country"),
        minWidth: 160,
      },
      {
        field: "expiryDate",
        columnName: t("vessel-call.create.table-headers.passport-expiry-date"),
        type: "date",
        minWidth: 180,
      },
    ],
    [t],
  );

  const toISO = (d?: Date | string) => {
    if (!d) return "";
    const dateObj = typeof d === "string" ? new Date(d) : d;
    return dateObj.toISOString();
  };

  const hasDuplicateCrewPassport = (passportNumber: string, currentId?: string) => {
    const normalizedPassport = normalizeCrewPassportNumber(passportNumber);

    return crew.some(
      (member) =>
        member.id !== currentId &&
        normalizeCrewPassportNumber(member.passportNumber) === normalizedPassport,
    );
  };

  const hasAnotherMaster = (rank: string, currentId?: string) => {
    if (!isMasterRank(rank)) {
      return false;
    }

    return crew.some((member) => member.id !== currentId && isMasterRank(member.rank));
  };

  const validateCrewSubmission = (data: CrewFormSchema) => {
    const validationErrors: Partial<Record<"rank" | "passportNumber", string>> = {};

    if (hasDuplicateCrewPassport(data.passportNumber, editingMember?.id)) {
      validationErrors.passportNumber = t("form-validation.duplicate-passport-number", {
        label: t("vessel-call.create.table-headers.passport-number"),
      });
    }

    if (hasAnotherMaster(String(data.rank ?? ""), editingMember?.id)) {
      validationErrors.rank = t("form-validation.master-rank-unique");
    }

    return Object.keys(validationErrors).length > 0 ? validationErrors : null;
  };

  const handleAddOrEditCrew = (data?: CrewFormSchema) => {
    if (!data) return;

    if (editingMember) {
      const updated: CrewMember = {
        ...editingMember,
        familyName: data.familyName,
        givenName: data.givenName,
        rank: String(data.rank ?? ""),
        nationality: data.nationality,
        dateOfBirth: toISO(data.dateOfBirth),
        placeOfBirth: data.placeOfBirth,
        gender: String(data.gender ?? ""),
        passportNumber: data.passportNumber,
        issuingState: data.issuingState,
        expiryDate: toISO(data.expiryDate),
      };

      useVesselCallFormStore.setState((s) => {
        const nextCrew = sortCrewWithMasterFirst(
          s.crew.map((member) => (member.id === updated.id ? updated : member)),
        );
        syncCrewField(nextCrew);
        return { ...s, crew: nextCrew };
      });

      setEditingMember(null);
      closeDrawer();
      return;
    }

    const newMember: CrewMember = {
      id: `crew-${Date.now()}`,
      familyName: data.familyName,
      givenName: data.givenName,
      rank: String(data.rank ?? ""),
      nationality: data.nationality,
      dateOfBirth: toISO(data.dateOfBirth),
      placeOfBirth: data.placeOfBirth,
      gender: String(data.gender ?? ""),
      passportNumber: data.passportNumber,
      issuingState: data.issuingState,
      expiryDate: toISO(data.expiryDate),
    };

    addCrew(newMember);
    const nextCrew = sortCrewWithMasterFirst(useVesselCallFormStore.getState().crew);
    useVesselCallFormStore.getState().setCrew(nextCrew);
    syncCrewField(nextCrew);
    setField(FORM_FIELDS.NUMBER_OF_CREW, String(nextCrew.length));
    setPage(1);
    clearAddCrewDraft();
    closeDrawer();
  };

  const onRowDelete = (row: CrewMember) => {
    useVesselCallFormStore.setState((s) => {
      const nextCrew = s.crew.filter((m) => m.id !== row.id);
      syncCrewField(nextCrew);
      return {
        ...s,
        crew: nextCrew,
        [FORM_FIELDS.NUMBER_OF_CREW]: String(Math.max(0, s.crew.length - 1)),
      };
    });

    setPage((prev) => Math.min(prev, Math.max(1, Math.ceil((crew.length - 1) / pageSize))));
  };

  const onRowEdit = (row: CrewMember) => {
    openEditDrawer(row);
  };

  const tableRows = useMemo(
    () =>
      [...crew]
        .sort(
          (left, right) =>
            (left.order ?? Number.POSITIVE_INFINITY) - (right.order ?? Number.POSITIVE_INFINITY),
        )
        .map((c, idx) => ({
          ...c,
          no: c.order ?? idx + 1,
          nationality: getNationalityNameByCode.get(c.nationality) ?? c.nationality,
          issuingState: getCountryNameByCode.get(c.issuingState) ?? c.issuingState,
        })),
    [crew, getNationalityNameByCode, getCountryNameByCode],
  );
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return tableRows.slice(start, end);
  }, [tableRows, page, pageSize]);

  const isEditing = Boolean(editingMember);

  return (
    <div className="relative p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-medium">{t("vessel-call.create.fal-form-5-title")}</h2>
        <div className="flex items-center gap-2">
          <input
            ref={importFileInputRef}
            type="file"
            hidden
            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleImportFileSelected}
            data-testid="falform5-import-file-input"
          />
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={importCrewListMutation.isPending}
            sx={{
              "borderColor": theme.palette.primary.main,
              "color": theme.palette.primary.main,
              "backgroundColor": theme.palette.common.white,
              "textTransform": "none",
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
            onClick={handleDownloadTemplate}
          >
            {t("vessel-call.create.toolbarActions.download-template")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DescriptionOutlinedIcon />}
            disabled={importCrewListMutation.isPending}
            sx={{
              "borderColor": theme.palette.primary.main,
              "color": theme.palette.primary.main,
              "backgroundColor": theme.palette.common.white,
              "textTransform": "none",
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
            onClick={openImportFileBrowser}
            data-testid="falform5-import-button"
          >
            {t("vessel-call.create.toolbarActions.import-xlsx")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ControlPointIcon />}
            disabled={importCrewListMutation.isPending}
            onClick={openAddDrawer}
            sx={{
              "borderColor": theme.palette.primary.main,
              "color": theme.palette.primary.main,
              "backgroundColor": theme.palette.common.white,
              "textTransform": "none",
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            {t("vessel-call.create.toolbarActions.add-crew")}
          </Button>
        </div>
      </div>

      {crew.length === 0 ? (
        <div className="mb-24">
          <NoTableEntries
            dataName={t("vessel-call.create.fal-form-5-member")}
            actionName={t("vessel-call.create.toolbarActions.add-crew")}
          />
        </div>
      ) : (
        <CustomDataTable
          title=""
          actionName={t("vessel-call.create.toolbarActions.add-crew")}
          rows={pagedRows}
          headers={headers}
          page={page}
          pageSize={pageSize}
          totalCount={tableRows.length}
          navigationLinkBase=""
          filters={[]}
          onApplyFilters={() => {}}
          availableFields={{}}
          onPageChange={(p: number, ps?: number) => {
            setPage(p);
            if (ps && ps !== pageSize) setPageSize(ps);
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          hideFilters={true}
          actionColumn={{
            onEditClick: onRowEdit,
            onDeleteClick: onRowDelete,
            width: 140,
          }}
        />
      )}
      {crewHasError && (
        <div className="mt-[-2.2rem]">
          <FormHelperText error>
            {t("vessel-call.create.form-fields.crewListValidation")}
          </FormHelperText>
        </div>
      )}
      <LoadingArrowOverlay loading={importCrewListMutation.isPending} />
      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        title={
          isEditing
            ? t("vessel-call.create.toolbarActions.edit-crew")
            : t("vessel-call.create.toolbarActions.add-crew")
        }
        description={
          isEditing
            ? t("vessel-call.create.toolbarActions.edit-crew")
            : t("vessel-call.create.toolbarActions.add-crew")
        }
        submitButtonLabel={
          isEditing
            ? t("vessel-call.create.toolbarActions.edit-crew")
            : t("vessel-call.create.toolbarActions.add-crew")
        }
        type={isEditing ? "editCrew" : "addCrew"}
        notesLabel={t("common.notes")}
        onSubmit={handleAddOrEditCrew}
        crewInitialValues={
          isEditing
            ? {
                familyName: editingMember!.familyName,
                givenName: editingMember!.givenName,
                rank: editingMember!.rank,
                nationality: editingMember!.nationality,
                placeOfBirth: editingMember!.placeOfBirth,
                dateOfBirth: editingMember!.dateOfBirth
                  ? new Date(editingMember!.dateOfBirth)
                  : undefined,
                gender: editingMember!.gender,
                passportNumber: editingMember!.passportNumber,
                issuingState: editingMember!.issuingState,
                expiryDate: editingMember!.expiryDate
                  ? new Date(editingMember!.expiryDate)
                  : undefined,
              }
            : (addCrewDraft ?? undefined)
        }
        validateCrewSubmission={validateCrewSubmission}
        onPersistDraft={handlePersistCrewDraft}
      />

      <GenericDialog
        open={Boolean(selectedImportFile)}
        title={t("vessel-call.create.fal-form-5.import-dialog-title")}
        elementContent={
          <div className="space-y-3">
            <p>
              {t("vessel-call.create.fal-form-5.import-dialog-description-prefix")}{" "}
              <strong>{selectedImportFile?.name ?? ""}</strong>{" "}
              {t("vessel-call.create.fal-form-5.import-dialog-description-suffix")}
            </p>
            {crew.length > 0 && (
              <p className="font-medium text-amber-700">
                {t("vessel-call.create.fal-form-5.import-dialog-overwrite-warning")}
              </p>
            )}
          </div>
        }
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("vessel-call.create.fal-form-5.import-dialog-confirm")}
        handleCloseDialog={closeImportDialog}
        confirmAction={handleImportConfirm}
        testId="falform5-import-confirm-dialog"
      />
    </div>
  );
};

export default FalForm5;
